<template>
  <div>
    <div :class="['editor-container', { 'is-from-modal': props.fromModal }]">
      <div v-if="!props.hideToolbar" :key="`toolbar-${toolbarRenderKey}`" :id="toolbarId" class="ql-toolbar ql-snow"
        :class="{ 'toolbar-readonly': props.readOnly }">
        <template v-for="(group, gi) in toolbarConfig" :key="gi">
          <span class="ql-formats">
            <template v-for="(item, ii) in group" :key="ii">
              <button v-if="typeof item === 'string'" :class="`ql-${item}`"></button>
              <select v-else-if="item.size" class="ql-size">
                <option v-for="opt in item.size" :key="String(opt)" :value="opt === false ? '' : opt"
                  :selected="opt === false">
                  {{ opt === false ? '' : opt }}
                </option>
              </select>
              <button v-else-if="item.list" class="ql-list" :value="item.list"></button>
              <button v-else-if="item.script" class="ql-script" :value="item.script"></button>
              <select v-else-if="item.color" class="ql-color"></select>
              <select v-else-if="item.background" class="ql-background"></select>
              <button v-else-if="item.align !== undefined" class="ql-align" :value="item.align"></button>
            </template>
          </span>
        </template>


        <div v-if="isCreation || props.showDeleteButton" class="toolbar-right">
          <div v-if="isCreation" class="mode-toggle-group">
            <button type="button" :class="['mode-toggle-btn', { 'is-active': mode === 'edit' }]" @click="mode = 'edit'">
              {{ authorLayout?.labels?.edit }}
            </button>

            <button type="button" :class="['mode-toggle-btn', { 'is-active': mode === 'select' }]"
              @click="mode = 'select'">
              {{ authorLayout?.labels?.select }}
            </button>
          </div>

          <button type="button" :class="['ql-milageFormat toolbar-add-btn', { 'is-hidden': !showAdd }]"
            v-html="addCircleOutlineSvg" @click="showAdd && emitSelected()" />

          <button v-if="props.showDeleteButton" type="button" class="toolbar-delete-btn" @click="$emit('delete-click')">
            <img v-if="props.deleteIcon" :src="props.deleteIcon" alt="delete" />
          </button>
        </div>
      </div>

      <QuillEditor :key="`${id}-${toolbarRenderKey}`" ref="editorRef"  v-model:content="content" content-type="delta" theme="snow"
        :options="editorOptions" :read-only="props.readOnly" />
    </div>



    <ModalEditor v-model="latexInput" :visible="showLatexModal" :operators="props.operators" @close="closeLatexModal"
      @confirm="applyLatex" :math-live-options="props.mathLiveOptions" :fromModal="props.fromModal"
      :teleport-target="props.teleportTarget" />

  </div>

</template>

<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  nextTick,
} from "vue";
import { QuillEditor, Quill } from "@vueup/vue-quill";
import "quill/dist/quill.snow.css";

import katex from "katex";
import "katex/dist/katex.min.css";
import Delta from "quill-delta";

import MilageLatexModal from "@/globals/modules/MilageLatexModal.vue";
import ModalEditor from "./ModalEditor.vue";
import { generateUniqueRandomKey } from "@/globals/helpers/randomKeys";
import { registerMilageFormats } from "@/globals/helpers/quillMilageFormats";
import { modalController } from "@ionic/vue";
import LinkVideoModal from "./LinkVideoModal.vue";
import OpenLinkModal from "./OpenLinkModal.vue";
import InsertUrlVideo from "./InsertUrlVideo.vue";
import { trimeSelectionRange } from "@/globals/helpers/trimSpaceDelta";
import { IWords, useWorksheetCreationStore } from "@/modules/j.createWorksheets/stores/createWksStore";
import { deltaEqualsByContent } from "@/modules/j.createWorksheets/helpers/deltaCompares";
import { getComputedPageLayout, IPageLayout } from "@/helpers/api";

registerMilageFormats();
(window as any).katex = katex;

const props = withDefaults(
  defineProps<{
    isCreation: boolean;
    hideToolbar?: boolean;
    initialContent: Delta | null | undefined;
    readOnly?: boolean;
    operators?: OperatorsMatrix | null;
    toolbarContent?: any;
    mathLiveOptions?: {
      showLatexMode?: boolean;
      showMenuToggle?: boolean;
      showVirtualKeyboard?: boolean;
      keyboardLayouts?: string[];
    } | null;
    showDeleteButton?: boolean;
    deleteIcon?: string;
    fromModal?: boolean;
    teleportTarget?: HTMLElement | null;
    audioRepetitions?: number;
    audioScopeKey?: string;
    placeholder?: string;
  }>(),
  {
    hideToolbar: false,
    readOnly: false,
    showDeleteButton: false,
    deleteIcon: "",
    fromModal: false,
    teleportTarget: null,
    audioRepetitions: 0,
    audioScopeKey: "",
  },
);

interface _2105 extends IPageLayout {
  labels: {
    edit: string;
    select: string;
  };
}

type Operator = [string, string];
type OperatorGroup = Operator[];
type OperatorsMatrix = OperatorGroup[];

const authorLayout = getComputedPageLayout<_2105>("2105");

const id = Math.random().toString(30).slice(2, 10);

const emit = defineEmits<{
  (e: "update:modelValue", value: any): void;
  (e: "selected-word", delta: Delta | null): void;
  (e: "editor-error"): void;
  (e: "words-list", words: IWords[]): void;
  (e: "delete-click"): void;
}>();

const Font = Quill.import("formats/font") as any;
Font.whitelist = ["calibri", "roboto", "serif", "monospace"];
Quill.register(Font, true); const KeyboardModule = Quill.import("modules/keyboard") as any;
delete KeyboardModule.DEFAULTS.bindings["list autofill"];

function toDelta(val: any): Delta | null {
  if (!val) return null;

  if (val instanceof Delta) return val;

  if (val?.ops && Array.isArray(val.ops)) {
    return new Delta(val.ops);
  }

  if (Array.isArray(val)) {
    return new Delta(val);
  }

  try {
    return new Delta(val);
  } catch {
    return null;
  }
}

function sameDelta(a: any, b: any) {
  const ao = a instanceof Delta ? a.ops : a?.ops;
  const bo = b instanceof Delta ? b.ops : b?.ops;
  return JSON.stringify(ao ?? null) === JSON.stringify(bo ?? null);
}

const content = ref<Delta>(
  prepareInitialContent(
    toDelta(props.initialContent) ?? new Delta(),
  ),
);

let isSyncingFromProps = false;

watch(
  () => [props.initialContent, props.audioRepetitions],
  ([val]) => {
    const rawNext = toDelta(val) ?? new Delta();

    const next = prepareInitialContent(rawNext);
    console.log("INITIAL_CONTENT_WATCH", { next, current: content.value });

    currentWordsList.value = [];
    listKeys.clear();
    listIds.clear();

    next.ops.forEach((op: any) => {
      const attrs = op.attributes;
      if (attrs?.milage && attrs?.key && attrs?.id) {
        currentWordsList.value.push({
          id: attrs.id,
          key: attrs.key,
          word: new Delta([op]),
        });
        listKeys.add(attrs.key);
        listIds.add(attrs.id);
      }
    });

    if (!sameDelta(content.value, next)) {
      // Apply silently through Quill first so its internal model already
      // matches `next` before the v-model watcher below sees the change -
      // otherwise vue-quill re-emits its own "update:content" (source
      // "api" still fires text-change) and the two components echo the
      // content back and forth forever.
      editorRef.value?.setContents(next, "silent");
      isSyncingFromProps = true;
      content.value = next;
    }

    console.log("Lista Words", currentWordsList.value);
    emit("words-list", currentWordsList.value);
  },
);

const debugText = ref<string>("");

watch(
  content,
  (val) => {
    if (isSyncingFromProps) {
      isSyncingFromProps = false;
    } else {
      emit("update:modelValue", val);
    }

    try {
      debugText.value = JSON.stringify(val, null, 2);
    } catch {
      debugText.value = String(val ?? "");
    }
  },
  { deep: true },
);

const editorRef = ref<InstanceType<typeof QuillEditor> | null>(null);

const showLatexModal = ref(false);
const latexInput = ref<string>("");

const cursorPosition = ref<number>(0);
const currentFormulaText = ref<string | null>("");
const currentFormulaIndex = ref<number | null>(null);
const selectedFormulaContent = ref<string | null>(null);
const selectedFormulaNode = ref<HTMLElement | null>(null);

const createWksStore = useWorksheetCreationStore();

function openLatexModalForNew() {
  const q = editorRef.value?.getQuill?.();
  if (!q) return;

  const selection = q.getSelection();
  const index = selection ? selection.index : q.getLength();
  cursorPosition.value = index;

  latexInput.value = "";
  currentFormulaIndex.value = null;
  selectedFormulaContent.value = null;
  showLatexModal.value = true;
}

function closeLatexModal() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  showLatexModal.value = false;
  q.setSelection(cursorPosition.value + 1, 0, "silent");

  if (selectedFormulaNode.value) {
    selectedFormulaNode.value.classList.remove("selected-formula");
    selectedFormulaNode.value = null;
  }
}

function stripSingleQuotes(s: string) {
  const t = s.trim();
  if (t.length >= 2 && t.startsWith("'") && t.endsWith("'"))
    return t.slice(1, -1);
  return t;
}

function deepNormalize(v: any): any {
  if (typeof v === "string") {
    const s = stripSingleQuotes(v);
    if (s === "true") return true;
    if (s === "false") return false;
    return s;
  }
  if (Array.isArray(v)) return v.map(deepNormalize);
  if (v && typeof v === "object") {
    const out: any = {};
    for (const k of Object.keys(v)) out[k] = deepNormalize(v[k]);
    return out;
  }
  return v;
}

function normalizeToolbarContent(raw: any): any | null {
  if (!raw) return null;

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return deepNormalize(parsed);
    } catch {
      return null;
    }
  }

  return deepNormalize(raw);
}

const normalizedToolbarContent = computed(() =>
  normalizeToolbarContent(props.toolbarContent),
);

const hasExternalToolbar = computed(() => {
  const cfg = normalizedToolbarContent.value;
  return !!cfg?.modules?.toolbar;
});

function ensureFormulaWrappers() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const root: HTMLElement = q.root;
  const formulas = root.querySelectorAll(".ql-formula");

  formulas.forEach((node) => {
    const formulaEl = node as HTMLElement;

    if (formulaEl.querySelector(".formula-hit-area")) return;

    const wrapper = document.createElement("span");
    wrapper.className = "formula-hit-area";

    while (formulaEl.firstChild) {
      wrapper.appendChild(formulaEl.firstChild);
    }

    formulaEl.appendChild(wrapper);
  });
}

function applyLatex() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const formula = latexInput.value || "";

  currentFormulaText.value = formula;

  if (currentFormulaIndex.value != null) {
    const index = currentFormulaIndex.value;
    // const test = q.getContents(index, 1, "user")
    // console.log("TESTE", test)
    // q.deleteText(index, 1, "user");
    // q.insertEmbed(index, "formula", formula, "user");
    const delta = new Delta().retain(index).delete(1).insert({ formula });

    q.updateContents(delta, "user");
  } else {
    const index = cursorPosition.value ?? q.getLength();
    q.insertEmbed(index, "formula", formula, "user");
  }

  if (selectedFormulaNode.value) {
    selectedFormulaNode.value.classList.remove("selected-formula");
    selectedFormulaNode.value = null;
  }

  currentFormulaIndex.value = null;
  closeLatexModal();
}

function handleFormulaClick(e: MouseEvent) {
  if (props.readOnly) return;

  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const target = (e.target as HTMLElement).closest(
    ".ql-formula",
  ) as HTMLElement | null;
  if (!target) return;
  console.log("DATA-VALUE", target.getAttribute("data-value"));

  currentFormulaText.value = target.getAttribute("data-value");

  const blot = Quill.find(target);
  const index = q.getIndex(blot);
  q.setSelection(index, 1, "silent");
  currentFormulaIndex.value = typeof index === "number" ? index : null;
  selectedFormulaContent.value = q.getSelection(index, 1);
  updateHasSelection();
  console.log("");
  //------
  if (selectedFormulaNode.value && selectedFormulaNode.value !== target) {
    selectedFormulaNode.value.classList.remove("selected-formula");
  }

  target.classList.add("selected-formula");
  selectedFormulaNode.value = target;
  //-------

  if (mode.value === "select") return;

  const formulaValue = target.getAttribute("data-value") || "";
  latexInput.value = formulaValue;

  showLatexModal.value = true;
}

function handleClickOutsideFormula(e: MouseEvent) {
  if (showLatexModal.value) return;

  const editorEl = document.querySelector(".editor-container");
  if (!editorEl) return;

  const clickedFormula = (e.target as HTMLElement).closest(".ql-formula");
  const clickedToolbar = (e.target as HTMLElement).closest(".ql-toolbar");

  if (!clickedFormula && !clickedToolbar && selectedFormulaNode.value) {
    selectedFormulaNode.value.classList.remove("selected-formula");
    selectedFormulaNode.value = null;
    currentFormulaIndex.value = null;
  }
}

function buildMilageAttrs(key: string, id: string) {
  return {
    background: "rgba(160, 210, 240, 0.2)",
    border: "1px solid #3ac0f2",
    br: "4px",
    milage: true,
    key,
    id,
    milage_id: id,
  };
}

const listKeys = new Set<string>(); // garantir keys unicas
const listIds = new Set<string>();// garantir ids unicas
const currentWordsList = ref<IWords[]>([]);

function ensureSpaceAfterSelection(
  q: any,
  range: { index: number; length: number },
) {
  const end = range.index + range.length;

  const nextChar = q.getText(end, 1);

  if (nextChar === " ") {
    q.setSelection(end + 1, 0, "silent");
    return;
  }
  q.insertText(end, " ", "silent");
  q.removeFormat(end, 1, "silent");
  q.setSelection(end + 1, 0, "silent");
}

function selectionHasForbidden(
  q: any,
  range: { index: number; length: number },
) {
  if (!range || range.length <= 0) return false;

  const delta = q.getContents(range.index, range.length);
  const ops = delta?.ops ?? [];

  for (const op of ops) {
    const insert = (op as any).insert;
    const attrs = (op as any).attributes;

    if (insert && typeof insert === "object") {
      if ("image" in insert) return true;
      if ("video" in insert) return true;
      if ("audioPlayer" in insert) return true;
    }
  }

  return false;
}

/**
 * O Quill só liga a toolbar (ícones, pickers de cor, handlers) quando é criado.
 * Se o toolbar-content chega depois (ex.: keyboard_latex do exame carregado
 * async), a toolbar era redesenhada pelo Vue sem o Quill a voltar a ligar -
 * ficavam selects nativos e botões sem ícone. Quando a configuração muda,
 * recria-se a toolbar e o editor (o conteúdo vem do v-model) e voltam a
 * ligar-se os eventos.
 */
const toolbarRenderKey = ref(0);

const toolbarConfig = computed(() => {

  const cfg = normalizedToolbarContent.value?.modules?.toolbar;
  if (Array.isArray(cfg)) return cfg;


  return [
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
    [{ script: "sub" }, { script: "super" }],
    [{ color: [] }, { background: [] }],
    [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
    ["link", "image", "video", "formula"],
    ["clean"],
  ];
});

watch(
  () => JSON.stringify(toolbarConfig.value),
  async (next, prev) => {
    if (next === prev) return;
    toolbarRenderKey.value++;
    await nextTick();
    bindQuillEvents();
  },
);

function emitSelected() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const range = q.getSelection();

  if (!range || range.length === 0) {
    emit("selected-word", null);
    return;
  }

  if (selectionHasForbidden(q, range)) {
    emit("editor-error");
    return;
  }

  //ok
  const trimed = trimeSelectionRange(q, range);

  const key = generateUniqueRandomKey(listKeys ?? new Set<string>());
  listKeys.add(key);

  const delta = q.getContents(trimed.index, trimed.length) as Delta;

  listKeys.add(key);
  const match = currentWordsList.value.find((w) =>
    deltaEqualsByContent(w.word, delta),
  );

  let idFinal: string;

  if (!match) {
    idFinal = generateUniqueRandomKey(listIds);
    listIds.add(idFinal);

    currentWordsList.value.push({
      id: idFinal,
      key,
      word: delta,
    });
  } else {
    idFinal = match.id;

    currentWordsList.value.push({
      id: idFinal,
      key,
      word: delta,
    });
  }

  const attrs = buildMilageAttrs(key, idFinal);

  q.formatText(trimed.index, trimed.length, attrs, "user");

  ensureSpaceAfterSelection(q, range);

  emit("words-list", currentWordsList.value);
}

function attachFormulaListeners() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const root: HTMLElement = q.root;

  ensureFormulaWrappers();

  const formulas = root.querySelectorAll(".ql-formula");
  formulas.forEach((el) => {
    el.removeEventListener("click", handleFormulaClick as any);
    el.addEventListener("click", handleFormulaClick as any);
    (el as HTMLElement).style.cursor = "pointer";
  });
}

function isMilage(attrs: any) {
  return attrs?.milage === true || attrs?.milage === "true";
}

function opLen(op: any) {
  const ins = op?.insert;
  return typeof ins === "string" ? ins.length : 1; // embed conta 1
}

// normaliza inserts (inclui fórmulas: { formula: "x^2" })
function normalizeInsert(insert: any) {
  if (typeof insert === "string") {
    let s = insert;
    if (s.endsWith("\r\n")) s = s.slice(0, -2);
    else if (s.endsWith("\n") || s.endsWith("\r")) s = s.slice(0, -1);
    return s;
  }
  return insert ?? null;
}

// gera “assinatura” só do conteúdo (ignora atributos)
function signatureFromOps(ops: any[]) {
  return JSON.stringify(ops.map((op) => normalizeInsert(op?.insert)));
}

function groupMilageOpsByKey(allOps: any[]) {
  const byKey = new Map<string, { id: string; ops: any[] }>();

  for (const raw of allOps) {
    const op = raw?._custom?.value ?? raw;
    const attrs = op?.attributes;

    if (!attrs || !isMilage(attrs) || !attrs.key) continue;

    const key = String(attrs.key);
    const id = String(attrs.id ?? "");

    const entry = byKey.get(key) ?? { id, ops: [] };
    if (!entry.id && id) entry.id = id; // mantém primeiro id não vazio
    entry.ops.push(op);

    byKey.set(key, entry);
  }

  return byKey;
}

function updateIdInEditorForKey(q: any, key: string, newId: string) {
  const sel = q.getSelection();

  const all = q.getContents();
  const ops = all.ops ?? [];

  let index = 0;
  for (const raw of ops) {
    const op = raw?._custom?.value ?? raw;
    const attrs = op?.attributes;

    const len = opLen(op);

    if (attrs && isMilage(attrs) && String(attrs.key) === String(key)) {
      // só troca o id, mantendo restantes attrs
      q.formatText(index, len, { id: newId }, "silent");
    }

    index += len;
  }

  if (sel) q.setSelection(sel.index, sel.length, "silent");
}

function handleWords() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  applyingWordFix = true;
  try {
    const current = q.getContents();
    const ops = current.ops ?? [];

    const grouped = groupMilageOpsByKey(ops);

    const oldSigToId = new Map<string, string>();
    for (const w of currentWordsList.value) {
      const sig = w.word?.ops ? signatureFromOps(w.word.ops) : "";
      const id = String(w.id ?? "");
      if (sig && id && !oldSigToId.has(sig)) oldSigToId.set(sig, id);
    }

    const keyToSig = new Map<string, string>();
    for (const [key, entry] of grouped.entries()) {
      keyToSig.set(key, signatureFromOps(entry.ops));
    }

    const sigToId = new Map<string, string>();
    const usedIdsBySig = new Map<string, string>();

    const reserveId = (sig: string, candidate: string) => {
      const prevSig = usedIdsBySig.get(candidate);
      if (!prevSig) {
        usedIdsBySig.set(candidate, sig);
        sigToId.set(sig, candidate);
        return candidate;
      }
      if (prevSig === sig) {
        sigToId.set(sig, candidate);
        return candidate;
      }
      return "";
    };

    for (const sig of new Set(keyToSig.values())) {
      if (!sig) continue;

      const oldPreferred = oldSigToId.get(sig);
      if (oldPreferred) {
        const ok = reserveId(sig, oldPreferred);
        if (ok) continue;
      }

      let editorCandidate = "";
      for (const [key, entry] of grouped.entries()) {
        if (keyToSig.get(key) === sig && entry.id) {
          editorCandidate = String(entry.id);
          break;
        }
      }
      if (editorCandidate) {
        const ok = reserveId(sig, editorCandidate);
        if (ok) continue;
      }

      const allExistingIds = new Set<string>([
        ...currentWordsList.value.map((w) => String(w.id)),
        ...Array.from(usedIdsBySig.keys()),
      ]);

      const newId = generateUniqueRandomKey(allExistingIds);
      reserveId(sig, newId);
    }

    const nextWords: IWords[] = [];
    const nextKeys = new Set<string>();
    const nextIds = new Set<string>();

    for (const [key, entry] of grouped.entries()) {
      const sig = keyToSig.get(key) ?? "";
      if (!sig) continue;

      const desiredId = sigToId.get(sig) ?? generateUniqueRandomKey(nextIds);
      const currentEditorId = String(entry.id ?? "");

      if (currentEditorId !== desiredId) {
        updateIdInEditorForKey(q, key, desiredId);
      }

      nextWords.push({
        key,
        id: desiredId,
        word: new Delta(entry.ops),
      });

      nextKeys.add(key);
      nextIds.add(desiredId);
    }

    currentWordsList.value = nextWords;

    listKeys.clear();
    listIds.clear();
    nextKeys.forEach((k) => listKeys.add(k));
    nextIds.forEach((id) => listIds.add(id));

    emit("words-list", currentWordsList.value);
  } finally {
    applyingWordFix = false;
  }
}

let pendingWords = false;
let applyingWordFix = false;

function scheduleHandleWords() {
  if (pendingWords) return;
  pendingWords = true;

  requestAnimationFrame(async () => {
    pendingWords = false;
    await nextTick(); // Vue atualiza refs/dom
    if (applyingWordFix) return; // se estivermos a corrigir ids, evita reentrância
    handleWords();
  });
}

function bindQuillEvents(): boolean {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return false;

  attachFormulaListeners();

  q.on("text-change", (_delta: any, _old: any, source: string) => {
    attachFormulaListeners();

    if (!props.isCreation) return;

    // ignora alterações "silent" (as tuas correções de id)
    if (source === "silent") return;

    scheduleHandleWords();
  });

  q.on(
    "selection-change",
    (range: any, oldRange: { index: number }, source: string) => {
      if (source === "user" && oldRange) {
        cursorPosition.value = oldRange.index;
      }

      updateHasSelection();
    },
  );

  return true;
}

onMounted(() => {
  if (!bindQuillEvents()) return;

  window.addEventListener("mousedown", handleClickOutsideFormula);

  //

  currentWordsList.value = [];
  listKeys.clear();
  listIds.clear();

  const next = prepareInitialContent(toDelta(props.initialContent) ?? new Delta());
  next.ops.forEach((op: any) => {
    const attrs = op.attributes;
    if (attrs?.milage && attrs?.key && attrs?.id) {
      currentWordsList.value.push({
        id: attrs.id,
        key: attrs.key,
        word: new Delta([op]),
      });
      listKeys.add(attrs.key);
      listIds.add(attrs.id);
    }
  });


});

onBeforeUnmount(() => {
  window.removeEventListener("mousedown", handleClickOutsideFormula);
});

const mode = ref<"edit" | "select">("edit");

const addCircleOutlineSvg = `
<svg viewBox="0 0 512 512" width="20" height="20" style="display:inline;vertical-align:middle">
  <circle cx="256" cy="256" r="208" fill="none" stroke="currentColor" stroke-width="32"/>
  <line x1="256" y1="176" x2="256" y2="336" stroke="currentColor" stroke-width="32" stroke-linecap="round"/>
  <line x1="176" y1="256" x2="336" y2="256" stroke="currentColor" stroke-width="32" stroke-linecap="round"/>
</svg>
`;

const toolbarId = `editor-toolbar-${Math.random().toString(36).slice(2)}`;

const audioSvg = `
<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"
  fill="none" stroke="currentColor" stroke-width="2"
  stroke-linecap="round" stroke-linejoin="round">
  <rect x="9" y="2" width="6" height="10" rx="3"></rect>
  <path d="M5 10v2a7 7 0 0 0 14 0v-2"></path>
  <path d="M12 19v3"></path>
</svg>
`;

const icons = Quill.import("ui/icons") as any;

icons.audio = audioSvg;

icons.audioPlayerBtn = audioSvg;

type AudioValue = {
  src: string;
  repeticoes?: number;
  podePausar?: boolean;
  audioKey: string;
};

function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

async function insertAudio() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const input = document.createElement("input");
  input.type = "file";
  input.accept = "audio/*";

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await fileToDataURL(file);

      const value: AudioValue = {
        src: dataUrl,
        repeticoes: 0,
        podePausar: true,
        audioKey: `${getAudioScopeKey()}::created-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}::${simpleHash(dataUrl)}`,
      };

      const index = q.getSelection(true)?.index ?? q.getLength();
      q.insertEmbed(index, "audioPlayer", value, "user");
      q.setSelection(index + 1, 0, "silent");
    } catch (err) {
      console.warn("Falha a ler áudio:", err);
    }
  };

  input.click();
}

function normalizeVideoUrl(raw: string) {
  const url = (raw ?? "").trim();
  if (!url) return "";

  const withProto = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)
    ? url
    : `https://${url}`;

  try {
    const u = new URL(withProto);

    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "");
      return id ? `https://www.youtube.com/embed/${id}` : withProto;
    }

    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v");
      if (id) return `https://www.youtube.com/embed/${id}`;
      if (u.pathname.startsWith("/embed/")) return withProto;
    }

    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
      if (u.hostname.includes("player.vimeo.com")) return withProto;
    }

    return withProto;
  } catch {
    return withProto;
  }
}

async function openVideoModal() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) return;

  const range = q.getSelection(true);
  const index = range?.index ?? q.getLength();

  const modal = await modalController.create({
    component: InsertUrlVideo,
    componentProps: {
      initialUrl: "",
    },
    backdropDismiss: true,
  });

  modal.onDidDismiss().then(({ data, role }) => {
    if (role !== "confirm") return;

    const raw = (data?.url ?? "").trim();
    if (!raw) return;

    const finalUrl = normalizeVideoUrl(raw);

    q.insertEmbed(index, "video", finalUrl, "user");

    if (data?.width) {
      q.formatText(index, 1, { width: data.width }, "user");
    }

    q.setSelection(index + 1, 0, "silent");
  });

  await modal.present();
}

const editorOptions = computed(() => {
  const handlers = {
    formula() { openLatexModalForNew(); },
    audioPlayerBtn() { insertAudio(); },
    audio() { insertAudio(); },
    video() { openVideoModal(); },
  };

  const baseModules: any = {
    formula: true,
    ...(props.readOnly ? {} : { imageResize: { modules: ["Resize", "DisplaySize"] } }),
  };

  const theme = normalizedToolbarContent.value?.theme ?? "snow";

  const toolbarModule = props.hideToolbar
    ? false
    : {
      container: `#${toolbarId}`,
      handlers,
    };

  return {
    theme,
    modules: {
      ...baseModules,
      toolbar: toolbarModule,
    },
    ...(props.placeholder ? { placeholder: props.placeholder } : {}),
  };
});

// O placeholder pode chegar depois do Quill estar montado (layouts async)
watch(
  () => props.placeholder,
  (val) => {
    const root = editorRef.value?.getQuill()?.root;
    if (root) root.dataset.placeholder = val ?? "";
  },
);



const hasSelection = ref(false);

function updateHasSelection() {
  const q: any = editorRef.value?.getQuill?.();
  if (!q) {
    hasSelection.value = false;
    return;
  }
  const r = q.getSelection();
  hasSelection.value = !!r && r.length > 0;
}

const showAdd = computed(() => {
  return props.isCreation && mode.value === "select" && hasSelection.value;
});

function normalizeAudioRepetitions(value?: number | null) {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

function simpleHash(value: string) {
  let hash = 0;

  const safeValue = `${value.length}:${value.slice(0, 5000)}`;

  for (let i = 0; i < safeValue.length; i++) {
    hash = (Math.imul(31, hash) + safeValue.charCodeAt(i)) | 0;
  }

  return Math.abs(hash).toString(36);
}

function getAudioScopeKey() {
  return props.audioScopeKey || `editor-${id}`;
}

function applyAudioSettings(delta: Delta, repetitions: number = 0) {
  const hasAudioPlayer = (delta.ops ?? []).some(
    (op: any) => op?.insert && typeof op.insert === "object" && "audioPlayer" in op.insert,
  );

  if (!hasAudioPlayer) return delta;

  const reps = normalizeAudioRepetitions(repetitions);
  const out = new Delta();

  const scopeKey = getAudioScopeKey();
  let audioIndex = 0;

  for (const op of delta.ops ?? []) {
    const insert = (op as any).insert;
    const attrs = (op as any).attributes;

    if (
      insert &&
      typeof insert === "object" &&
      "audioPlayer" in insert
    ) {
      const audioPlayer = insert.audioPlayer;
      const src = String(audioPlayer.src ?? "");
      const currentAudioIndex = audioIndex++;

      out.insert(
        {
          audioPlayer: {
            ...audioPlayer,

            repeticoes: reps,

            podePausar:
              reps > 0
                ? false
                : audioPlayer.podePausar !== false,

            audioKey: `${scopeKey}::audio-${currentAudioIndex}::${simpleHash(src)}`,
          },
        },
        attrs,
      );

      continue;
    }

    out.insert(insert, attrs);
  }

  return out;
}

function prepareInitialContent(delta: Delta) {
  return applyAudioSettings(delta, props.audioRepetitions);
}
</script>

<style scoped>
.editor-container {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0px 0px 6px 6px;
}

.ql-simple .ql-editor {
  min-height: 150px;
  font-size: 16px;
}

.editor-container :deep(.ql-toolbar.ql-snow) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  flex-wrap: wrap;

  overflow-x: visible;

  white-space: normal;

  border-radius: 6px 6px 0 0;
}

.editor-container :deep(.ql-container.ql-snow) {
  border: 1px solid #d1d5db !important;
  border-radius: 0px 0px 6px 6px;
  height: auto;
}

.ql-simple :deep(.ql-editor) {
  overflow-y: visible;
}

.editor-container :deep(.formula-hit-area) {
  display: inline-block;
  padding: 2px 4px;
  border-radius: 5px;
  transition: background 0.2s, box-shadow 0.2s;
}

.editor-container :deep(.formula-hit-area:hover) {
  background: #ededed;
  box-shadow: 0 0 0 2px #5acaff55;
  cursor: pointer;
}

.editor-container :deep(.ql-formula.selected-formula .formula-hit-area) {
  background: #ededed;
  box-shadow: 0 0 0 2px #5acaff55;
}

.toolbar-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.toolbar-right {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* botão com + */
.toolbar-add-btn {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.mode-toggle-group {
  display: inline-flex;
  width: fit-content;
  border-radius: 999px;
  background: #e5e7eb;
  padding: 5px;
  gap: 2px;
}

.mode-toggle-btn {
  flex: 0 0 auto;
  min-width: max-content;
  padding: 4px 12px;
  white-space: nowrap;
  text-align: center;
  border: none;
  background: transparent;
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  line-height: 1.2;
  box-sizing: border-box;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.mode-toggle-btn.is-active {
  background: #00acdf;
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
}

.editor-container :deep(.ql-audio-wrapper) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e3e7ed;
  border-radius: 10px;
  background: #fff;
}

.ql-snow.ql-toolbar button,
.ql-snow .ql-toolbar button {
  height: 26px;
}

.editor-container :deep(.ql-audio-wrapper .audio-ui) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.editor-container :deep(.ql-audio-wrapper .audio-play) {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #cfd6dd;
  background: #f7fafc;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.editor-container :deep(.ql-audio-wrapper .audio-time),
.editor-container :deep(.ql-audio-wrapper .audio-reps) {
  font-size: 0.9rem;
  color: #445;
}

:deep(.ql-editor .ql-video) {
  aspect-ratio: 16/9 !important;
}

.editor-container :deep(.ql-font-calibri) {
  font-family: Calibri, "Carlito", "Segoe UI", Arial, sans-serif;
}

.editor-container :deep(.ql-font-roboto) {
  font-family: Roboto, system-ui, -apple-system, "Segoe UI", Arial, sans-serif;
}

.editor-container :deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="calibri"]::before),
.editor-container :deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="calibri"]::before) {
  content: "Calibri";
  font-family: Calibri, "Carlito", "Segoe UI", Arial, sans-serif;
}

.editor-container :deep(.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="roboto"]::before),
.editor-container :deep(.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="roboto"]::before) {
  content: "Roboto";
  font-family: Roboto, system-ui, -apple-system, "Segoe UI", Arial, sans-serif;
}

.editor-container :deep(.ql-toolbar.ql-snow .mode-toggle-btn.is-active:hover) {
  background: #00abdf7a !important;
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
  box-shadow: none !important;
  outline: none !important;

}

.editor-container {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: visible;
  position: relative;
}

.editor-container :deep(.ql-toolbar.ql-snow) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  overflow: visible !important;
  white-space: normal;
  border: 0 !important;
  border-bottom: 1px solid #d1d5db !important;
  border-radius: 6px 6px 0 0;
  position: relative;
  z-index: 20;
}

.editor-container :deep(.ql-container.ql-snow) {
  border: 0 !important;
  border-radius: 0 0 6px 6px;
  height: auto;
}

.editor-container :deep(.ql-snow .ql-picker) {
  position: relative;
}



.editor-container :deep(.ql-snow .ql-picker.ql-expanded .ql-picker-options) {
  top: auto !important;
  bottom: calc(100% + 6px) !important;
  z-index: 9999 !important;
}

.editor-container.is-from-modal :deep(.ql-snow .ql-picker.ql-expanded .ql-picker-options) {
  top: calc(100% + 6px) !important;
  bottom: auto !important;
}

.toolbar-delete-btn {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.toolbar-delete-btn img {
  width: 16px;
  height: 16px;
}

.toolbar-add-btn.is-hidden {
  visibility: hidden;
  pointer-events: none;
}

.toolbar-readonly {
  pointer-events: none;
  opacity: 0.55;
}
</style>
